import { prepareChartFonts } from '@terminal/views/alfred/research/utils/copyAlfredCharts';
import {
  inlineComputedStyles,
  resetChartNodeCloneForSerialization,
} from '@terminal/views/alfred/research/utils/copyChartDom';

import {
  hoistSvgForeignObjects,
  replaceSvgsWithPngs,
} from './prepareClipboardSvg';
import { transferCanvasesToClone } from './prepareDrawerContent';
import {
  embedExternalImages,
  swapImagesToLightTheme,
} from './rasterizeClipboardImage';
import {
  expandCatalystBadgesForClipboard,
  rasterizeClipboardSnapshots,
} from './rasterizeClipboardSnapshots';
import {
  rasterizeXhtmlToPng,
  serializeElementToXhtml,
} from './rasterizeHtmlElement';

const DRAWER_RASTERIZE_TIMEOUT_MS = 15_000;
const CLIPBOARD_IMAGE_PADDING_PX = 24;

const PASTE_STYLE_PROPERTIES = [
  'color',
  'background-color',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'line-height',
  'text-align',
  'text-decoration',
  'margin-top',
  'margin-right',
  'margin-bottom',
  'margin-left',
  'padding-top',
  'padding-right',
  'padding-bottom',
  'padding-left',
] as const;

const LAYOUT_WRAPPER_TAGS = new Set([
  'DIV',
  'SECTION',
  'ARTICLE',
  'HEADER',
  'MAIN',
  'ASIDE',
  'SPAN',
]);

const parsePx = (value: string) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const hasDirectText = (el: Element) =>
  Array.from(el.childNodes).some(
    node => node.nodeType === Node.TEXT_NODE && !!node.textContent?.trim()
  );

const addMargin = (
  element: HTMLElement,
  side: 'margin-top' | 'margin-left',
  extra: number
) => {
  if (!extra) {
    return;
  }
  const current = parsePx(
    window.getComputedStyle(element).getPropertyValue(side)
  );
  element.style.setProperty(side, `${current + extra}px`);
};

const flattenFlexGaps = (root: HTMLElement) => {
  const elements = [
    root,
    ...Array.from(root.querySelectorAll<HTMLElement>('*')),
  ];

  elements.forEach(el => {
    const computed = window.getComputedStyle(el);
    if (computed.display !== 'flex' && computed.display !== 'inline-flex') {
      return;
    }

    const isColumn = computed.flexDirection.startsWith('column');
    const extra = parsePx(isColumn ? computed.rowGap : computed.columnGap);
    if (!extra) {
      return;
    }

    const side = isColumn ? 'margin-top' : 'margin-left';
    Array.from(el.children).forEach((child, index) => {
      if (index > 0 && child instanceof HTMLElement) {
        addMargin(child, side, extra);
      }
    });
  });
};

const replacePasteHeadingsWithDivs = (root: HTMLElement) => {
  // Docs applies default paragraph spacing to <p>/<h*> even when margin:0 is
  // inlined; <div> keeps the copied font styles without that extra gap.
  root.querySelectorAll('p,h1,h2,h3,h4,h5,h6').forEach(el => {
    const div = document.createElement('div');
    Array.from(el.attributes).forEach(attr => {
      div.setAttribute(attr.name, attr.value);
    });
    while (el.firstChild) {
      div.appendChild(el.firstChild);
    }
    el.replaceWith(div);
  });
};

const isTransparentOrWhite = (color: string) => {
  const normalized = color.replace(/\s/g, '').toLowerCase();
  return (
    normalized === 'transparent' ||
    normalized === 'rgba(0,0,0,0)' ||
    normalized === 'rgb(255,255,255)' ||
    normalized === '#ffffff' ||
    normalized === '#fff'
  );
};

const isLayoutOnlyWrapper = (el: HTMLElement) => {
  if (
    !LAYOUT_WRAPPER_TAGS.has(el.tagName) ||
    el.hasAttribute('data-clipboard-preserve') ||
    hasDirectText(el)
  ) {
    return false;
  }

  const computed = window.getComputedStyle(el);
  if (
    computed.display.includes('flex') &&
    !computed.flexDirection.startsWith('column')
  ) {
    return false;
  }
  if (
    parsePx(computed.paddingTop) > 1 ||
    parsePx(computed.paddingBottom) > 1 ||
    parsePx(computed.borderTopWidth) > 0
  ) {
    return false;
  }

  return isTransparentOrWhite(computed.backgroundColor);
};

const convertMarginBottomToNextMarginTop = (root: HTMLElement) => {
  Array.from(root.querySelectorAll<HTMLElement>('*')).forEach(el => {
    const marginBottom = parsePx(el.style.marginBottom);
    if (!marginBottom) {
      return;
    }
    const next = el.nextElementSibling;
    if (next instanceof HTMLElement) {
      addMargin(next, 'margin-top', marginBottom);
    }
    el.style.marginBottom = '0px';
  });
};

const unwrapLayoutWrappers = (root: HTMLElement) => {
  const candidates = Array.from(
    root.querySelectorAll<HTMLElement>(
      'div,section,article,header,main,aside,span'
    )
  ).reverse();

  candidates.forEach(el => {
    if (el === root || !root.contains(el) || !isLayoutOnlyWrapper(el)) {
      return;
    }

    const children = Array.from(el.childNodes);
    if (children.length === 0) {
      el.remove();
      return;
    }

    const marginTop = parsePx(el.style.marginTop);
    const firstElement = children.find(
      (node): node is HTMLElement => node instanceof HTMLElement
    );
    if (firstElement) {
      addMargin(firstElement, 'margin-top', marginTop);
    }
    el.replaceWith(...children);
  });
};

const fitClipboardImagesForPaste = (root: HTMLElement) => {
  root.querySelectorAll('img').forEach(img => {
    img.style.display = 'block';
    img.style.verticalAlign = 'top';
    img.style.marginBottom = '0px';
  });
};

const inlinePasteStyles = (root: HTMLElement) => {
  const elements = Array.from(
    root.querySelectorAll<HTMLElement | SVGElement>('*')
  );
  elements.forEach(el => {
    const computed = window.getComputedStyle(el);
    const skipLineHeight = !hasDirectText(el) && el.childElementCount > 0;
    PASTE_STYLE_PROPERTIES.forEach(property => {
      if (property === 'line-height' && skipLineHeight) {
        return;
      }
      el.style.setProperty(property, computed.getPropertyValue(property));
    });
  });
};

const normalizeClipboardPaste = (root: HTMLElement) => {
  flattenFlexGaps(root);
  replacePasteHeadingsWithDivs(root);
  inlinePasteStyles(root);
  convertMarginBottomToNextMarginTop(root);
  unwrapLayoutWrappers(root);
  fitClipboardImagesForPaste(root);
};

const expandAccordionHeights = (root: HTMLElement) => {
  root
    .querySelectorAll('[data-testid="transition-wrapper"]')
    .forEach(wrapper => {
      if (!(wrapper instanceof HTMLElement)) {
        return;
      }

      // Gmail clips accordion bodies that still have the live widget's inline
      // height:0 / grid 0fr; Google Docs ignores those constraints and shows
      // the text. Expand every panel so both paste destinations match.
      wrapper.style.height = 'auto';
      wrapper.style.maxHeight = 'none';
      wrapper.style.overflow = 'visible';
      wrapper.style.gridTemplateRows = '1fr';

      wrapper
        .querySelectorAll<HTMLElement>('[data-accordion-content]')
        .forEach(content => {
          content.style.height = 'auto';
          content.style.maxHeight = 'none';
          content.style.overflow = 'visible';
        });
    });
};

const hideCloneFromView = (clone: HTMLElement) => {
  clone.style.position = 'fixed';
  clone.style.left = '-99999px';
  clone.style.top = '0';
  clone.style.opacity = '';
  clone.style.visibility = '';
  clone.style.pointerEvents = 'none';
  // Source nodes may be `display: contents` (no box). Off-screen
  // positioning only applies if the clone generates a box; otherwise
  // its children paint in document flow when appended to body.
  clone.style.display = 'block';
};

const getClipboardSourceWidth = (source: HTMLElement) =>
  source.offsetWidth || source.parentElement?.clientWidth || 0;

const rasterizeCloneToPng = async ({
  clone,
  fontCss,
}: {
  clone: HTMLElement;
  fontCss: string;
}): Promise<string | undefined> => {
  clone.style.padding = `${CLIPBOARD_IMAGE_PADDING_PX}px`;
  clone.style.boxSizing = 'border-box';

  const width = Math.round(clone.offsetWidth);
  const height = Math.round(clone.scrollHeight);

  inlineComputedStyles(clone);
  resetChartNodeCloneForSerialization(clone);
  const xhtml = serializeElementToXhtml(clone);
  hideCloneFromView(clone);

  return rasterizeXhtmlToPng({
    xhtml,
    width,
    height,
    fontCss,
    timeoutMs: DRAWER_RASTERIZE_TIMEOUT_MS,
  });
};

export const prepareClipboardHtml = async (
  source: HTMLElement,
  headerHtml?: string
) => {
  const clone = source.cloneNode(true) as HTMLElement;
  transferCanvasesToClone(source, clone);
  hideCloneFromView(clone);
  clone.style.width = `${getClipboardSourceWidth(source)}px`;
  clone.style.backgroundColor = '#ffffff';
  clone.style.color = 'var(--text-default)';
  clone.setAttribute('aria-hidden', 'true');
  clone.classList.add('light');
  clone.setAttribute('data-theme', 'light_default');
  clone.dataset.clipboardClone = '';

  document.body.appendChild(clone);

  try {
    const fontCss = await prepareChartFonts();
    expandAccordionHeights(clone);
    expandCatalystBadgesForClipboard(clone);
    swapImagesToLightTheme(clone);
    hoistSvgForeignObjects(clone);
    await replaceSvgsWithPngs(clone, fontCss);
    await embedExternalImages(clone);
    await rasterizeClipboardSnapshots(clone, fontCss);
    normalizeClipboardPaste(clone);

    if (headerHtml) {
      clone.insertAdjacentHTML('afterbegin', headerHtml);
    }

    return {
      html: clone.innerHTML,
      toPng: () => rasterizeCloneToPng({ clone, fontCss }),
      dispose: () => clone.remove(),
    };
  } catch (error) {
    clone.remove();
    throw error;
  }
};
